import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import { 
  Send as SendIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import ChatIcon from '../../assets/Chat.svg';
import EditIcon from '../../assets/Edit.svg';
import TrashIcon from '../../assets/Trash.svg';
import AlertDialog from '../Common/AlertDialog';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

// Helper function to handle profile picture URL (same as DashboardLayout)
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

const TaskDetailCommentBlock = ({ comments = [], taskId, currentUserId, employees = [] }) => {
  const { mode } = useThemeMode();
  const [commentText, setCommentText] = useState('');
  const [localComments, setLocalComments] = useState(comments);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [editingCommentIndex, setEditingCommentIndex] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Mention functionality state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchText, setMentionSearchText] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const inputRef = React.useRef(null);

  // Helper function to format datetime consistently
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'No date';
    
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      // Format: "11:44 AM, 31 Aug 2025"
      const time = date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      const datePart = `${day} ${month} ${year}`;
      
      return `${time}, ${datePart}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Handle comment text change with @ mention detection
  const handleCommentTextChange = (e) => {
    const text = e.target.value;
    setCommentText(text);

    // Get cursor position
    const cursorPosition = e.target.selectionStart;
    
    // Find the last @ symbol before cursor
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Check if there's a space between @ and cursor
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        // We're in mention mode
        setMentionSearchText(textAfterAt.toLowerCase());
        setShowMentionDropdown(true);
        
        // Filter users based on search text
        const filtered = employees.filter(emp => 
          (emp.full_name?.toLowerCase().includes(textAfterAt.toLowerCase()) ||
           emp.username?.toLowerCase().includes(textAfterAt.toLowerCase()))
        ).slice(0, 5); // Limit to 5 suggestions
        
        setFilteredUsers(filtered);
        setSelectedMentionIndex(0);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // Handle mention selection
  const selectMention = (user) => {
    const cursorPosition = inputRef.current.selectionStart;
    const textBeforeCursor = commentText.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textBefore = commentText.substring(0, lastAtIndex);
      const textAfter = commentText.substring(cursorPosition);
      const mentionText = `@${user.username || user.full_name} `;
      
      const newText = textBefore + mentionText + textAfter;
      setCommentText(newText);
      setShowMentionDropdown(false);
      
      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = (textBefore + mentionText).length;
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        inputRef.current.focus();
      }, 0);
    }
  };

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = (e) => {
    if (!showMentionDropdown) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev => 
        prev < filteredUsers.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter' && filteredUsers.length > 0) {
      e.preventDefault();
      selectMention(filteredUsers[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
    }
  };

  // Render comment text with mentions highlighted
  const renderCommentWithMentions = (text) => {
    if (!text) return text;
    
    // Support two patterns:
    // 1) @username
    // 2) @[__66__](__raj2__) where 66 is user_id and raj2 is username
    const combinedRegex = /@\[__([0-9]+)__\]\(__([^_()]+)__\)|@(\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = combinedRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      const userId = match[1];
      const usernameFromBracket = match[2];
      const usernameSimple = match[3];
      const displayName = usernameFromBracket || usernameSimple || '';
      
      parts.push(
        <span
          key={match.index}
          data-user-id={userId || undefined}
          style={{
            color: '#26C3C0',
            fontWeight: '500',
            backgroundColor: mode === 'dark' ? 'rgba(38, 195, 192, 0.1)' : 'rgba(38, 195, 192, 0.15)',
            padding: '2px 4px',
            borderRadius: '4px',
            display: 'inline'
          }}
        >
          @{displayName}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 1 ? (
      <React.Fragment>
        {parts.map((part, index) => (
          <React.Fragment key={index}>{part}</React.Fragment>
        ))}
      </React.Fragment>
    ) : text;
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !taskId) return;

    try {
      setIsSubmitting(true);
      const res = await dataAPI.postTaskComment(taskId, commentText.trim());
      
      // Get current user data from localStorage
      let currentUserData = null;
      try {
        const userData = localStorage.getItem('userData');
        if (userData) {
          currentUserData = JSON.parse(userData);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }

      const newComment = res?.data || {
        id: Date.now(),
        user_id: currentUserId,
        username: currentUserData?.username || currentUserData?.full_name || 'Unknown User',
        full_name: currentUserData?.full_name || currentUserData?.username || 'Unknown User',
        comment: commentText.trim(),
        created_at: new Date().toISOString(),
        profile_pic: currentUserData?.profile_pic || null
      };
      
      setLocalComments([newComment, ...localComments]);
      setCommentText('');
    } catch (err) {
      console.error('Failed to post comment', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get display name
  const getDisplayName = (comment) => {
    return comment.full_name || comment.username || 'Unknown User';
  };

  // Helper function to get profile picture
  const getProfilePic = (comment) => {
    return comment.profile_pic || null;
  };

  // Helper function to get comment text
  const getCommentText = (comment) => {
    return comment.comment || comment.comment_text || comment.text || 'No comment text';
  };

  // Helper function to truncate comment text
  const getTruncatedComment = (comment) => {
    const text = getCommentText(comment);
    const maxLines = 4;
    const maxChars = maxLines * 50; // Approximate characters per line
    
    if (text.length <= maxChars) {
      return { text, isTruncated: false };
    }
    
    return { 
      text: text.substring(0, maxChars) + '...', 
      isTruncated: true 
    };
  };

  // Handle comment expansion
  const handleCommentToggle = (commentId) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId);
    } else {
      newExpanded.add(commentId);
    }
    setExpandedComments(newExpanded);
  };

  // Handle delete comment click
  const handleDeleteComment = (comment, index) => {
    setCommentToDelete({ ...comment, index });
    setDeleteDialogOpen(true);
  };

  // Confirm delete comment
  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;
    
    // Clear any existing delete error
    setDeleteError('');
    
    try {
      // Use comment.id if available, otherwise use index for local removal
      if (commentToDelete.id) {
        await dataAPI.deleteTaskComment(commentToDelete.id);
      }
      
      // Remove comment from local state
      setLocalComments(prev => prev.filter((_, idx) => idx !== commentToDelete.index));
      
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
      // Show custom toast error instead of browser alert
      setDeleteError('Failed to delete comment. Please try again.');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setDeleteError('');
      }, 5000);
      // Close the delete dialog
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  };

  // Handle edit comment click
  const handleEditComment = (comment, index) => {
    setEditingCommentIndex(index);
    setEditingCommentText(comment.comment || '');
  };

  // Handle save edit comment
  const handleSaveEditComment = async (comment, index) => {
    if (!editingCommentText.trim()) return;
    
    try {
      // Call API to update comment if comment has an ID
      if (comment.id) {
        await dataAPI.updateTaskComment(comment.id, editingCommentText.trim());
      }
      
      // Update local state
      setLocalComments(prev => prev.map((c, idx) => 
        idx === index ? { ...c, comment: editingCommentText.trim() } : c
      ));
      
      // Exit edit mode
      setEditingCommentIndex(null);
      setEditingCommentText('');
    } catch (error) {
      console.error('Error updating comment:', error);
      alert('Failed to update comment. Please try again.');
    }
  };

  // Handle cancel edit comment
  const handleCancelEditComment = () => {
    setEditingCommentIndex(null);
    setEditingCommentText('');
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (commentIndex) => {
    setActiveDropdown(activeDropdown === commentIndex ? null : commentIndex);
  };

  // Handle dropdown close
  const handleDropdownClose = () => {
    setActiveDropdown(null);
  };

  // Calculate textarea height based on content
  const calculateTextareaHeight = (text) => {
    const lines = text.split('\n').length;
    const baseHeight = 20; // line height in pixels
    const padding = 24; // top and bottom padding (12px each)
    const minHeight = baseHeight + padding; // 1 line
    const maxHeight = (baseHeight * 3) + padding; // 3 lines
    const calculatedHeight = (baseHeight * lines) + padding;
    
    return Math.min(Math.max(calculatedHeight, minHeight), maxHeight);
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .comments-scroll::-webkit-scrollbar {
            width: 6px;
          }
          .comments-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .comments-scroll::-webkit-scrollbar-thumb {
            background: ${mode === 'dark' ? '#555' : '#ccc'};
            border-radius: 3px;
          }
          .comments-scroll::-webkit-scrollbar-thumb:hover {
            background: ${mode === 'dark' ? '#777' : '#999'};
          }
          .comments-scroll {
            scrollbar-width: thin;
            scrollbar-color: ${mode === 'dark' ? '#555' : '#ccc'} transparent;
          }
        `}
      </style>
      
      <div style={{
        backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        borderRadius: '12px',
        border: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
          gridRow: '1'
        }}>
          <button
            style={{
              flex: 1,
              padding: '16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: '#26C3C0',
              fontFamily: 'Urbanist, Arial, sans-serif',
              borderBottom: '2px solid #26C3C0',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center'
            }}
          >
            <img
              src={ChatIcon}
              alt="Comments"
              style={{
                width: '16px',
                height: '16px',
                filter: 'brightness(0) saturate(100%) invert(70%) sepia(100%) saturate(1000%) hue-rotate(160deg) brightness(0.8)',
                opacity: 0.9
              }}
            />
            Comments
          </button>
        </div>

        {/* Comments Content - Scrollable Area */}
        <div 
          className="comments-scroll"
          style={{ 
            gridRow: '2',
            padding: '20px', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '520px', // Reduced height to 580px
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <style>
            {`
              .comments-scrollable-area::-webkit-scrollbar {
                display: none;
              }
              .comments-scrollable-area {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
            `}
          </style>
          {/* Comments List - Scrollable only when needed */}
          <div 
            className="comments-scrollable-area"
            style={{ 
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: '20px'
            }}
          >
            {localComments.length > 0 ? (
              localComments.map((comment, index) => (
                <div key={comment.id || index} style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '20px',
                  paddingBottom: '20px',
                  borderBottom: index < localComments.length - 1 ? `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}` : 'none',
                  position: 'relative'
                }}>
                  {/* User Avatar */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#26C3C0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontWeight: '600',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {getProfilePic(comment) ? (
                      <img
                        src={getProfilePicUrl(getProfilePic(comment))}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          // Replace the image with a fallback avatar when it fails to load
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.style.cssText = `
                            width: 100%;
                            height: 100%;
                            border-radius: 50%;
                            background-color: #26C3C0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: #FFFFFF;
                            font-size: 16px;
                            font-weight: 600;
                          `;
                          fallbackDiv.textContent = getDisplayName(comment).charAt(0).toUpperCase();
                          e.target.parentNode.replaceChild(fallbackDiv, e.target);
                        }}
                      />
                    ) : (
                      getDisplayName(comment).charAt(0).toUpperCase()
                    )}
                  </div>

                  {/* Comment Content */}
                  <div style={{ 
                    flex: 1, 
                    minWidth: 0, 
                    width: 0, 
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}>
                    {/* User Info and Timestamp */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: 0,
                        flex: 1,
                        overflow: 'hidden'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: mode === 'dark' ? '#CCCCCC' : '#666666',
                        fontFamily: 'Urbanist, Arial, sans-serif'
                      }}>
                        {getDisplayName(comment)}
                      </span>
                      <span style={{
                        fontSize: '12px',
                        color: mode === 'dark' ? '#999999' : '#999999',
                        fontFamily: 'Urbanist, Arial, sans-serif'
                      }}>
                        •
                      </span>
                      <span style={{
                        fontSize: '12px',
                        color: mode === 'dark' ? '#999999' : '#999999',
                        fontFamily: 'Urbanist, Arial, sans-serif'
                      }}>
                        {comment.created_at ? 
                          formatDateTime(comment.created_at) : 
                          'Just now'
                        }
                      </span>
                    </div>
                    
                      {/* Options Menu - Only show for comments owned by current user */}
                      {comment.user_id === currentUserId && (
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => handleDropdownToggle(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Options"
                            onMouseEnter={(e) => e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <MoreVertIcon style={{
                              fontSize: '16px',
                              color: mode === 'dark' ? '#ffffff' : '#666666',
                              opacity: 0.7
                            }} />
                          </button>

                          {/* Dropdown Menu */}
                          {activeDropdown === index && (
                            <>
                              {/* Backdrop to close dropdown */}
                              <div 
                                style={{
                                  position: 'fixed',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 999
                                }}
                                onClick={handleDropdownClose}
                              />
                              
                              {/* Dropdown Content */}
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                backgroundColor: mode === 'dark' ? '#333' : '#ffffff',
                                border: `1px solid ${mode === 'dark' ? '#555' : '#e0e0e0'}`,
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 1000,
                                minWidth: '120px',
                                overflow: 'hidden'
                              }}>
                                {/* Edit Option */}
                                <button
                                  onClick={() => {
                                    handleEditComment(comment, index);
                                    handleDropdownClose();
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: mode === 'dark' ? '#ffffff' : '#222222',
                                    fontFamily: 'Urbanist, Arial, sans-serif',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                  <img
                                    src={EditIcon}
                                    alt="Edit"
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      filter: 'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(500%) hue-rotate(160deg) brightness(90%) contrast(120%)',
                                      opacity: 0.7
                                    }}
                                  />
                                  Edit
                                </button>

                                {/* Delete Option */}
                                <button
                                  onClick={() => {
                                    handleDeleteComment(comment, index);
                                    handleDropdownClose();
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: mode === 'dark' ? '#ffffff' : '#222222',
                                    fontFamily: 'Urbanist, Arial, sans-serif',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f5f5f5'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                  <img
                                    src={TrashIcon}
                                    alt="Delete"
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      filter: 'brightness(0) saturate(100%) invert(27%) sepia(92%) saturate(6084%) hue-rotate(340deg) brightness(98%) contrast(87%)',
                                      opacity: 0.7
                                    }}
                                  />
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Comment Text or Edit Box */}
                    {editingCommentIndex === index ? (
                      <div style={{ position: 'relative', width: '100%', maxWidth: '100%', minWidth: 0 }}>
                        {/* Edit Text Area */}
                        <textarea
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            height: `${calculateTextareaHeight(editingCommentText)}px`,
                            fontSize: '14px',
                            color: mode === 'dark' ? '#FFFFFF' : '#222222',
                            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                            border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                            borderRadius: '8px',
                            padding: '12px',
                            fontFamily: 'Urbanist, Arial, sans-serif',
                            lineHeight: '20px',
                            resize: 'none',
                            outline: 'none',
                            overflow: 'auto',
                            boxSizing: 'border-box'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#26C3C0';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = mode === 'dark' ? '#444' : '#ddd';
                          }}
                        />
                        
                        {/* Save/Cancel Icons */}
                        <div style={{
                          display: 'flex',
                          gap: '4px',
                          marginTop: '3px',
                          justifyContent: 'flex-end'
                        }}>
                          {/* Save Icon */}
                          <button
                            onClick={() => handleSaveEditComment(comment, index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Save comment"
                            onMouseEnter={(e) => e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <SaveIcon style={{
                              fontSize: '16px',
                              color: '#26C3C0'
                            }} />
                          </button>
                          
                          {/* Cancel Icon */}
                          <button
                            onClick={handleCancelEditComment}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Cancel edit"
                            onMouseEnter={(e) => e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CloseIcon style={{
                              fontSize: '16px',
                              color: mode === 'dark' ? '#ffffff' : '#666666'
                            }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Regular Comment Display */
                    <div style={{
                      fontSize: '14px',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      fontFamily: 'Urbanist, Arial, sans-serif',
                      lineHeight: '1.4',
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}>
                      {(() => {
                        const commentId = comment.id || index;
                        const isExpanded = expandedComments.has(commentId);
                        const { text, isTruncated } = getTruncatedComment(comment);
                        
                        return (
                          <div>
                            <div style={{
                              whiteSpace: isExpanded ? 'pre-wrap' : 'normal',
                              wordBreak: 'break-word'
                            }}>
                              {isExpanded ? renderCommentWithMentions(getCommentText(comment)) : renderCommentWithMentions(text)}
                            </div>
                            {isTruncated && (
                              <button
                                onClick={() => handleCommentToggle(commentId)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#26C3C0',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '4px 0',
                                  marginTop: '4px',
                                  fontFamily: 'Urbanist, Arial, sans-serif',
                                  textDecoration: 'underline'
                                }}
                              >
                                {isExpanded ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    )}
                  </div>


                </div>
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: mode === 'dark' ? '#999' : '#666',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                <div style={{
                  marginBottom: '16px',
                  opacity: 0.5
                }}>
                  <img
                    src={ChatIcon}
                    alt="No Comments"
                    style={{
                      width: '48px',
                      height: '48px',
                      filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                      opacity: 0.3
                    }}
                  />
                </div>
                No comments yet
              </div>
            )}
          </div>
        </div>

        {/* Comment Input - Sticky at Bottom */}
        <form onSubmit={handleCommentSubmit} style={{
          gridRow: '3',
          padding: '16px',
          borderTop: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          position: 'relative'
        }}>
          {/* Mention Dropdown */}
          {showMentionDropdown && filteredUsers.length > 0 && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: '16px',
              right: '16px',
              marginBottom: '8px',
              backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
              border: `1px solid ${mode === 'dark' ? '#3a3a3a' : '#e0e0e0'}`,
              borderRadius: '8px',
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000
            }}>
              {filteredUsers.map((user, index) => (
                <div
                  key={user.id}
                  onClick={() => selectMention(user)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: index === selectedMentionIndex 
                      ? (mode === 'dark' ? '#2a2a2a' : '#f5f5f5') 
                      : 'transparent',
                    transition: 'background-color 0.2s ease',
                    borderBottom: index < filteredUsers.length - 1 
                      ? `1px solid ${mode === 'dark' ? '#2a2a2a' : '#f0f0f0'}` 
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = mode === 'dark' ? '#2a2a2a' : '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    if (index !== selectedMentionIndex) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {/* User Avatar */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#26C3C0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: '600',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {user.profile_pic ? (
                      <img
                        src={getProfilePicUrl(user.profile_pic)}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.textContent = (user.full_name || user.username || 'U').charAt(0).toUpperCase();
                        }}
                      />
                    ) : (
                      (user.full_name || user.username || 'U').charAt(0).toUpperCase()
                    )}
                  </div>
                  
                  {/* User Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      fontFamily: 'Urbanist, Arial, sans-serif',
                      marginBottom: '2px'
                    }}>
                      {user.full_name || user.username}
                    </div>
                    {user.username && user.full_name && (
                      <div style={{
                        fontSize: '12px',
                        color: mode === 'dark' ? '#999' : '#666',
                        fontFamily: 'Urbanist, Arial, sans-serif'
                      }}>
                        @{user.username}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
            border: `1px solid ${mode === 'dark' ? '#3a3a3a' : '#e0e0e0'}`,
            borderRadius: '12px',
            padding: '12px 48px 12px 44px',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box'
          }}>
            {/* Left Icon */}
            <div style={{
              position: 'absolute',
              left: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              color: mode === 'dark' ? '#8a8a8a' : '#9e9e9e'
            }}>
              <img
                src={ChatIcon}
                alt="Comment"
                style={{
                  width: '18px',
                  height: '18px',
                  filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
                  opacity: 0.7
                }}
              />
            </div>

            {/* Input */}
            <input
              ref={inputRef}
              type="text"
              value={commentText}
              onChange={handleCommentTextChange}
              onKeyDown={handleKeyDown}
              placeholder="Add a comment... (use @ to mention)"
              style={{
                flex: 1,
                minWidth: 0,
                width: 0,
                maxWidth: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: mode === 'dark' ? '#FFFFFF' : '#222222',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
                boxSizing: 'border-box'
              }}
            />

            {/* Send Button or Progress Indicator */}
            {isSubmitting ? (
              <div
                style={{
                  position: 'absolute',
                  right: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: `1px solid ${mode === 'dark' ? '#3a3a3a' : '#e0e0e0'}`,
                  backgroundColor: mode === 'dark' ? '#1f1f1f' : '#ffffff'
                }}
              >
                {/* Mini Circular Progress */}
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: `2px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
                  borderTop: '2px solid #26C3C0',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              </div>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  position: 'absolute',
                  right: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: `1px solid ${mode === 'dark' ? '#3a3a3a' : '#e0e0e0'}`,
                  backgroundColor: mode === 'dark' ? '#1f1f1f' : '#ffffff',
                  cursor: 'pointer',
                  color: mode === 'dark' ? '#9e9e9e' : '#666666',
                  transition: 'background-color 0.2s ease, color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  const icon = e.currentTarget.querySelector('svg');
                  if (icon) {
                    icon.style.color = '#26C3C0';
                  }
                }}
                onMouseLeave={(e) => {
                  const icon = e.currentTarget.querySelector('svg');
                  if (icon) {
                    icon.style.color = mode === 'dark' ? '#9e9e9e' : '#666666';
                  }
                }}
              >
                <SendIcon 
                  style={{
                    fontSize: '18px',
                    color: mode === 'dark' ? '#9e9e9e' : '#666666'
                  }}
                />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Alert Dialog for deletion confirmation */}
      <AlertDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteComment}
        type="delete"
        title="Confirm Deletion"
        message={`Are you sure you want to delete this comment? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Delete Error Toast */}
      {deleteError && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#1a0000',
          border: '1px solid #ff4444',
          borderRadius: '8px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(255, 68, 68, 0.2)',
          zIndex: 2000,
          minWidth: '350px',
          maxWidth: '400px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <WarningIcon style={{
              color: '#ff4444',
              fontSize: '20px'
            }} />
            <span style={{
              color: '#ffffff',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1.4'
            }}>
              {deleteError}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default TaskDetailCommentBlock;
